import {
  ExpenseEntry,
  ExpenseFormData,
  mapExpenseEntryToApiRequest,
} from "../../vehicleExpense/components/types";
import { fixedIncomeApi } from "../apiService";

export const handleFixedExpenseSubmit = async (
  values: ExpenseFormData,
  initialValues: ExpenseFormData,
  id: string,
  fixedJourneyId: string,
  setIsSubmitting: (isSubmitting: boolean) => void,
  showToast: (message: string, type: "success" | "error") => void,
  onSuccess: () => void,
  vehicle?: any
) => {
  try {
    setIsSubmitting(true);
    let promises: Promise<any>[] = [];

    // Get expenses to add
    const expensesToAdd = values.expenses.filter((expense) => !expense?.id);
    if (expensesToAdd.length > 0) {
      const expenseRequests = expensesToAdd.map((expense) =>
        mapExpenseEntryToApiRequest(expense, values.vehicleNumber)
      );
      if (expenseRequests.length > 0) {
        promises.push(
          fixedIncomeApi.createBulkExpense(
            expenseRequests,
            id || fixedJourneyId
          )
        );
      }
    }

    // Get expenses to update
    const expensesToUpdate = values.expenses.filter(
      (expense) => expense?.isEdited && expense?.id
    );
    if (expensesToUpdate.length > 0) {
      const expenseRequests = expensesToUpdate.map((expense) =>
        mapExpenseEntryToApiRequest(expense, values.vehicleNumber)
      );
      if (expenseRequests.length > 0) {
        promises.push(fixedIncomeApi.updateBulkExpense(expenseRequests));
      }
    }

    // Get expenses to delete
    const expensesToChangedDelete = expensesToUpdate
      .map((changed) =>
        initialValues.expenses.find((initial) => initial.id === changed.id)
      )
      .filter(Boolean) // Filters out undefined/null
      .map((expense) => ({ ...expense, isEdited: true })) as ExpenseEntry[];

    const expensesToSoftDelete = values.expenses.filter(
      (expense) => expense?.isDeleted
    );

    const expensesToDelete = [
      ...expensesToChangedDelete,
      ...expensesToSoftDelete,
    ];

    if (expensesToDelete.length > 0) {
      const expenseRequests = expensesToDelete.map((expense) => {
        return mapExpenseEntryToApiRequest(expense, values.vehicleNumber);
      });
      if (expenseRequests.length > 0) {
        promises.push(
          fixedIncomeApi.deleteBulkExpense(
            expenseRequests,
            id || fixedJourneyId
          )
        );
      }
    }

    // Get payments to create
    const paymentToCreate = values.expenses.flatMap(
      (expense) =>
        expense.payments?.flatMap((payment) => {
          if (!payment?.id && expense.id) {
            return { ...payment, fixedJourneyExpenseId: expense.id };
          }
          return [];
        }) ?? []
    );
    if (paymentToCreate.length > 0) {
      promises.push(fixedIncomeApi.bulkCreateExpensePayment(paymentToCreate));
    }

    // Get payments to update
    const paymentToUpdate = values.expenses.flatMap((expense) => {
      return expense.payments?.flatMap((payment) => {
        if (payment?.id && payment?.isEdited) {
          return payment;
        }
        return [];
      });
    });
    if (paymentToUpdate.length > 0) {
      promises.push(fixedIncomeApi.bulkUpdateExpensePayment(paymentToUpdate));
    }

    // Get payments to create
    const initialPayment = initialValues.expenses.flatMap((expense) =>
      expense.payments?.flatMap((payment) => {
        if (payment?.id) {
          return payment;
        }
        return null;
      })
    );

    const paymentToCreatedDelete = paymentToUpdate.map((changed) =>
      initialPayment?.find((initial) => initial?.id === changed?.id)
    );
    if (paymentToCreatedDelete.length > 0) {
      promises.push(
        fixedIncomeApi.bulkDeleteExpensePayment(paymentToCreatedDelete)
      );
    }

    const paymentToSoftDelete = values.expenses.flatMap((expense) =>
      expense.payments?.flatMap((payment) => {
        if (payment?.isDeleted) {
          return payment;
        }
        return [];
      })
    );
    if (paymentToSoftDelete.length > 0) {
      promises.push(
        fixedIncomeApi.bulkDeleteExpensePayment(paymentToSoftDelete)
      );
    }

    if (promises.length > 0) {
      const results = await Promise.all(promises);
      showToast("Expenses saved successfully", "success");
    }

    setIsSubmitting(false);
    onSuccess();
  } catch (error) {
    console.error("Error saving expenses:", error);
    showToast("Error saving expenses", "error");
    setIsSubmitting(false);
  }
};
